import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { TokenSearch } from "@/components/discovery/TokenSearch";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CoinGrid } from "@/components/coins/CoinGrid";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Globe, Rocket, Vote, Zap, Shield } from "lucide-react";

export default function Discover() {
  const { data: platformCoins, isLoading: loadingPlatform } = useQuery({
    queryKey: ["discover-platform-coins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .order("market_cap", { ascending: false })
        .limit(24);
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <>
      <Helmet>
        <title>Discover Tokens - TheMoonRun</title>
        <meta name="description" content="Discover all tokens on Solana. Search platform coins, explore external tokens, and vote on new listings." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
              <Globe className="w-6 h-6 sm:w-7 sm:h-7 text-blue-500" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-foreground">Discover & Trade</h1>
              <p className="text-sm text-muted-foreground">Explore all tokens on Solana</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <Badge variant="outline" className="bg-success/10 text-success">
              <Zap className="w-3 h-3 mr-1" />
              Best Price
            </Badge>
            <Badge variant="outline" className="bg-primary/10 text-primary">
              <Shield className="w-3 h-3 mr-1" />
              Community Protected
            </Badge>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <TokenSearch />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="platform">
          <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:flex mb-6">
            <TabsTrigger value="platform" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Rocket className="w-4 h-4" />
              <span className="hidden sm:inline">Platform</span> Coins
            </TabsTrigger>
            <TabsTrigger value="external" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Globe className="w-4 h-4" />
              External
            </TabsTrigger>
            <TabsTrigger value="voting" className="gap-1 sm:gap-2 text-xs sm:text-sm">
              <Vote className="w-4 h-4" />
              Vote
            </TabsTrigger>
          </TabsList>

          <TabsContent value="platform">
            <CoinGrid coins={platformCoins ?? []} isLoading={loadingPlatform} />
          </TabsContent>

          <TabsContent value="external">
            <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
              <h3 className="font-semibold text-foreground mb-2">External Token Trading</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Trade any Solana token through our DEX aggregator. Paste any token address to get the best rates.
              </p>
              <TokenSearch />
            </div>
          </TabsContent>

          <TabsContent value="voting">
            <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
              <h3 className="font-semibold text-foreground mb-2">Community Voting</h3>
              <p className="text-sm text-muted-foreground">
                Vote on tokens to help protect the community from scams. Locked tokens are flagged as potentially dangerous.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <Zap className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-semibold mb-1">Jupiter Aggregation</h3>
            <p className="text-sm text-muted-foreground">Best prices across all DEXs</p>
          </div>
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <Shield className="w-8 h-8 text-success mb-3" />
            <h3 className="font-semibold mb-1">Community Safety</h3>
            <p className="text-sm text-muted-foreground">Vote to lock scam tokens</p>
          </div>
          <div className="p-4 sm:p-6 rounded-xl bg-card border border-border">
            <Globe className="w-8 h-8 text-accent mb-3" />
            <h3 className="font-semibold mb-1">Any Token</h3>
            <p className="text-sm text-muted-foreground">Import by address</p>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
